// Run lifecycle: the player's traversal from spawn to the timer-end trigger.
// Owns the state machine (idle → running → finished) and the wall-clock
// timestamps. HUD observes; main.ts drives via tick().
//
// Pure over plain {x, y, z} shapes so tests don't need THREE.

export type RunPhase = 'idle' | 'running' | 'finished'

export type RunState =
  | { phase: 'idle' }
  | { phase: 'running'; startSec: number }
  | { phase: 'finished'; startSec: number; endSec: number }

export interface XYZ {
  x: number
  y: number
  z: number
}

export interface RunPlayer {
  position: XYZ
  velocity: XYZ
}

export interface RunWorld {
  endTriggerCenter: XYZ
  endTriggerHalfSize: XYZ
}

// Horizontal-speed threshold (u/s) that flips idle → running. Below this we
// treat the player as still on the spawn pad.
export const RUN_START_SPEED = 5

export class Run {
  state: RunState = { phase: 'idle' }

  tick(player: RunPlayer, world: RunWorld, nowSec: number): void {
    if (this.state.phase === 'idle') {
      const horizSpeed = Math.hypot(player.velocity.x, player.velocity.z)
      if (horizSpeed > RUN_START_SPEED) {
        this.state = { phase: 'running', startSec: nowSec }
      }
      return
    }
    if (this.state.phase === 'running' && insideTrigger(player.position, world)) {
      this.state = {
        phase: 'finished',
        startSec: this.state.startSec,
        endSec: nowSec,
      }
    }
  }

  reset(): void {
    this.state = { phase: 'idle' }
  }

  elapsed(nowSec: number): number {
    if (this.state.phase === 'running') return nowSec - this.state.startSec
    if (this.state.phase === 'finished') return this.state.endSec - this.state.startSec
    return 0
  }
}

function insideTrigger(p: XYZ, world: RunWorld): boolean {
  const c = world.endTriggerCenter
  const h = world.endTriggerHalfSize
  return (
    Math.abs(p.x - c.x) <= h.x &&
    Math.abs(p.y - c.y) <= h.y &&
    Math.abs(p.z - c.z) <= h.z
  )
}
